import * as React from 'react';
import classnames from 'classnames';
import Base, { Props as BaseProps, TypeSizes } from '../Base/Base';
import './Flex.css';

export interface Props extends BaseProps {
  /**
   * Used on a parent, sets both the horizontal and vertical alignment of flex children.
   * Must be used in conjuction with the 'direction' prop.
   * */
  alignChildren?: 'start' | 'middle' | 'end';
  /**
   * Used on a parent, sets both the horizontal alignment of flex children.
   * Must be used in conjuction with the 'direction' prop.
   * */
  alignChildrenHorizontal?: 'start' | 'middle' | 'end' | 'around' | 'between';
  /**
   * Used on a parent, sets both the vertical alignment of flex children.
   * Must be used in conjuction with the 'direction' prop.
   * */
  alignChildrenVertical?: 'start' | 'middle' | 'end' | 'around' | 'between' | 'baseline';
  /**
   * Set the alignment of a flex child. Must be used inside a parent
   * using the 'direction' prop.
   */
  alignSelf?: 'start' | 'middle' | 'end';
  /** @ignore */
  className?: string;
  /** Used on a parent, activates flex behaviour for children. */
  direction?: 'horizontal' | 'vertical';
  /** Used on a parent, reverses the layout direction  */
  reverse?: boolean;
  /**
   * Used on a parent, sets the distances between flex children to
   * a multiple value. E.g. 'x1'.
   */
  gap?: TypeSizes;
  /** Sets a child to grow from it's initial width. (flex-grow: 1;) */
  grow?: boolean | 'x1' | 'x2' | 'x3' | 'x4';
  /** Sets a childs initial width before flex calculations. (flex-basis)  */
  initial?: 'content' | 'container' | 'none';
  /** Sets a child to shrink from it's initial width. (flex-shrink: 1;) */
  shrink?: boolean | 'x1' | 'x2' | 'x3' | 'x4';
  /** Used on a parent, allows it's children to wrap onto new rows.. */
  wrap?: boolean;
}

/**
 * A useful and quick Flex component for handling
 * frequent and common layouts of all sizes.
 */
const Flex: React.FunctionComponent<Props> = (props: Props) => {
  const {
    alignChildren,
    alignChildrenHorizontal = alignChildren,
    alignChildrenVertical = alignChildren,
    alignSelf,
    className,
    direction,
    reverse,
    grow,
    gap,
    initial,
    shrink,
    wrap,
    ...rest
  } = props;

  const classes = classnames('Flex', {
    [`Flex--align-horz-${alignChildrenHorizontal}`]: alignChildrenHorizontal,
    [`Flex--align-self-${alignSelf}`]: alignSelf,
    [`Flex--align-vert-${alignChildrenVertical}`]: alignChildrenVertical,
    [`Flex--${direction}`]: direction,
    [`Flex--initial-${initial}`]: initial,
    'Flex--gapped': gap,
    [`Flex--gap-${gap}`]: gap,
    'Flex--reverse': reverse,
    'Flex--grow': grow === true,
    [`Flex--grow-${grow}`]: typeof grow === 'string',
    'Flex--shrink': shrink === true,
    [`Flex--shrink-${shrink}`]: typeof shrink === 'string',
    'Flex--wrap': wrap,
  }, className);

  return (
    <Base { ...rest } className={ classes } />
  );
};

export default Flex;
